// Generic virtualized table for the 10k-row lists (Stores, Team, Territories, report tables): TanStack Table v8 for
// columns and sorting, TanStack Virtual for the body, so only the rows in view (plus overscan) are in the DOM.
// - Sorting: click a header (Shift+click adds a secondary sort); controlled or internal.
// - Selection (optional): a checkbox column, header select-all with the indeterminate state, Shift+click range
//   (on the checkbox or the row), Cmd/Ctrl+click toggles one row; controlled by ids so selections survive re-sorts
//   and replica updates.
// - Rows: click handler, keyboard (Up/Down/Home/End/PageUp/PageDown move, Enter opens, Space selects), one tab stop.
// - Sticky header, horizontal scroll for wide column sets, skeleton rows while loading, an empty state.
// The table fills its parent's height (give the parent a height or flex-1 min-h-0) unless `height` is set.

import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnDef,
  type Row,
  type SortingState,
  type Updater,
  type VisibilityState,
} from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';
import { ArrowDown, ArrowUp, ChevronsUpDown, Inbox } from 'lucide-react';
import {
  useCallback,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
  type MouseEvent,
  type ReactNode,
} from 'react';
import { cn } from './cn';
import { EmptyState } from './Display';
import { Skeleton } from './Feedback';

/** Per-column presentation, set as `meta` on a column def. */
export interface DataTableColumnMeta {
  align?: 'left' | 'center' | 'right';
  /** Share of the spare width this column takes (flex-grow). Columns without it keep their `size`. */
  grow?: number;
  headerClassName?: string;
  cellClassName?: string;
}

// TanStack's own docs type heterogeneous column lists with `any` for the value type (each column has its own).
export type DataTableColumn<T> = ColumnDef<T, any>;

export interface DataTableProps<T> {
  data: readonly T[];
  columns: readonly DataTableColumn<T>[];
  /** Stable id per row (the item id); selection and keys use it. */
  getRowId: (row: T, index: number) => string;
  /** Accessible name of the table ("Stores"). */
  label: string;

  /** Controlled sorting; omit to let the table keep its own (starting from `initialSorting`). */
  sorting?: SortingState;
  onSortingChange?: (sorting: SortingState) => void;
  initialSorting?: SortingState;
  /** false turns header sorting off (a column can also opt out with `enableSorting: false`). */
  enableSorting?: boolean;
  /** The caller sorts `data` itself (e.g. by a domain comparator); headers still show and report state. */
  manualSorting?: boolean;
  columnVisibility?: VisibilityState;

  /** Show the checkbox column. */
  selectable?: boolean;
  /** Controlled selection (row ids). Omit for internal selection state. */
  selectedIds?: ReadonlySet<string>;
  onSelectionChange?: (ids: Set<string>) => void;
  isRowSelectable?: (row: T) => boolean;

  onRowClick?: (row: T, event: MouseEvent<HTMLElement> | KeyboardEvent<HTMLElement>) => void;
  /** Highlights one row (the one open in a side panel). */
  activeRowId?: string | null;
  getRowClassName?: (row: T) => string | undefined;

  /** Fixed row height in px (virtualization needs it). Default 48. */
  rowHeight?: number;
  /** Container height (CSS length or px). Default: fill the parent. */
  height?: number | string;
  overscan?: number;
  loading?: boolean;
  /** Shown when there are no rows (and not loading). */
  emptyState?: ReactNode;
  className?: string;
}

const SELECT_COL_WIDTH = 44;
const HEADER_HEIGHT = 40;

function metaOf<T>(column: { columnDef: DataTableColumn<T> }): DataTableColumnMeta {
  return (column.columnDef.meta as DataTableColumnMeta | undefined) ?? {};
}

function cellStyle(size: number, grow: number | undefined): CSSProperties {
  return { flex: `${grow ?? 0} 0 ${size}px`, minWidth: size, width: size };
}

const ALIGN: Record<NonNullable<DataTableColumnMeta['align']>, string> = {
  left: 'justify-start text-left',
  center: 'justify-center text-center',
  right: 'justify-end text-right',
};

/** Clicks on these inside a row are the control's own, not a row click. */
const INTERACTIVE = 'a,button,input,select,textarea,label,[role="button"],[role="menuitem"],[data-row-action]';

function applyUpdater<S>(updater: Updater<S>, previous: S): S {
  return typeof updater === 'function' ? (updater as (old: S) => S)(previous) : updater;
}

export function DataTable<T>({
  data,
  columns,
  getRowId,
  label,
  sorting: sortingProp,
  onSortingChange,
  initialSorting,
  enableSorting = true,
  manualSorting = false,
  columnVisibility,
  selectable = false,
  selectedIds: selectedProp,
  onSelectionChange,
  isRowSelectable,
  onRowClick,
  activeRowId,
  getRowClassName,
  rowHeight = 48,
  height,
  overscan = 10,
  loading = false,
  emptyState,
  className,
}: DataTableProps<T>) {
  const [innerSorting, setInnerSorting] = useState<SortingState>(initialSorting ?? []);
  const sorting = sortingProp ?? innerSorting;
  const [innerSelected, setInnerSelected] = useState<Set<string>>(() => new Set());
  const selected: ReadonlySet<string> = selectedProp ?? innerSelected;
  const anchorId = useRef<string | null>(null);
  const [focusIndex, setFocusIndex] = useState(0);
  const scrollRef = useRef<HTMLDivElement>(null);

  const setSorting = useCallback(
    (updater: Updater<SortingState>) => {
      const next = applyUpdater(updater, sorting);
      if (sortingProp === undefined) setInnerSorting(next);
      onSortingChange?.(next);
    },
    [sorting, sortingProp, onSortingChange],
  );

  const setSelected = useCallback(
    (next: Set<string>) => {
      if (selectedProp === undefined) setInnerSelected(next);
      onSelectionChange?.(next);
    },
    [selectedProp, onSelectionChange],
  );

  const table = useReactTable<T>({
    data: data as T[],
    columns: columns as DataTableColumn<T>[],
    getRowId: (row, index) => getRowId(row, index),
    state: { sorting, ...(columnVisibility ? { columnVisibility } : null) },
    onSortingChange: setSorting,
    enableSorting,
    manualSorting,
    enableMultiSort: true,
    isMultiSortEvent: (e) => (e as MouseEvent).shiftKey === true,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: manualSorting ? undefined : getSortedRowModel(),
  });

  const rows = table.getRowModel().rows;
  const headerGroups = table.getHeaderGroups();
  const leafColumns = table.getVisibleLeafColumns();
  const totalWidth = leafColumns.reduce((sum, c) => sum + c.getSize(), 0) + (selectable ? SELECT_COL_WIDTH : 0);
  const interactive = !!onRowClick || selectable;

  const canSelect = useCallback((row: Row<T>) => (isRowSelectable ? isRowSelectable(row.original) : true), [
    isRowSelectable,
  ]);

  const selectableRows = useMemo(() => (selectable ? rows.filter(canSelect) : []), [selectable, rows, canSelect]);
  const selectedVisible = selectableRows.reduce((n, r) => n + (selected.has(r.id) ? 1 : 0), 0);
  const allSelected = selectableRows.length > 0 && selectedVisible === selectableRows.length;
  const someSelected = selectedVisible > 0 && !allSelected;

  const toggleAll = () => {
    const next = new Set(selected);
    if (allSelected) for (const r of selectableRows) next.delete(r.id);
    else for (const r of selectableRows) next.add(r.id);
    setSelected(next);
  };

  /** Toggle one row, or with `range` set every row between the anchor and it to the clicked row's new state. */
  const toggleRow = (index: number, range: boolean) => {
    const row = rows[index];
    if (!row || !canSelect(row)) return;
    const next = new Set(selected);
    const target = !selected.has(row.id);
    const anchorIndex = anchorId.current ? rows.findIndex((r) => r.id === anchorId.current) : -1;
    if (range && anchorIndex >= 0) {
      const [from, to] = anchorIndex <= index ? [anchorIndex, index] : [index, anchorIndex];
      for (let i = from; i <= to; i += 1) {
        const r = rows[i]!;
        if (!canSelect(r)) continue;
        if (target) next.add(r.id);
        else next.delete(r.id);
      }
    } else if (target) {
      next.add(row.id);
    } else {
      next.delete(row.id);
    }
    anchorId.current = row.id;
    setSelected(next);
  };

  const virtualizer = useVirtualizer({
    count: loading ? 0 : rows.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => rowHeight,
    overscan,
    getItemKey: (i) => rows[i]?.id ?? i,
    // The body starts below the header inside the same scroll box: scrollMargin puts the rows' offsets where they
    // really are (item.start includes it; rows are drawn at start - scrollMargin within the body), so the visible
    // range and scrollToIndex are right. Without it, moving down with the keyboard scrolls the focused row 40px too
    // little and leaves it hidden under the bottom edge.
    scrollMargin: HEADER_HEIGHT,
    // The sticky header covers the top 40px of the viewport: scrolling up to a row stops just below it.
    scrollPaddingStart: HEADER_HEIGHT,
  });
  const items = virtualizer.getVirtualItems();
  const firstRendered = items[0]?.index ?? 0;
  const lastRendered = items[items.length - 1]?.index ?? -1;
  const safeFocus = Math.min(focusIndex, Math.max(0, rows.length - 1));
  const focusRowRendered = safeFocus >= firstRendered && safeFocus <= lastRendered;

  const focusRow = (index: number) => {
    const clamped = Math.max(0, Math.min(rows.length - 1, index));
    setFocusIndex(clamped);
    virtualizer.scrollToIndex(clamped, { align: 'auto' });
    // The row may only exist after the virtualizer re-renders.
    requestAnimationFrame(() => {
      scrollRef.current?.querySelector<HTMLElement>(`[data-row-index="${clamped}"]`)?.focus({ preventScroll: true });
    });
  };

  const pageSize = Math.max(1, Math.floor(((scrollRef.current?.clientHeight ?? 480) - HEADER_HEIGHT) / rowHeight) - 1);

  const onRowKeyDown = (event: KeyboardEvent<HTMLElement>, index: number) => {
    if (event.target !== event.currentTarget) return; // keys inside cell controls are theirs
    const row = rows[index]!;
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        if (event.shiftKey && selectable) toggleRow(index + 1, true);
        focusRow(index + 1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        if (event.shiftKey && selectable) toggleRow(index - 1, true);
        focusRow(index - 1);
        break;
      case 'Home':
        event.preventDefault();
        focusRow(0);
        break;
      case 'End':
        event.preventDefault();
        focusRow(rows.length - 1);
        break;
      case 'PageDown':
        event.preventDefault();
        focusRow(index + pageSize);
        break;
      case 'PageUp':
        event.preventDefault();
        focusRow(index - pageSize);
        break;
      case 'Enter':
        if (onRowClick) {
          event.preventDefault();
          onRowClick(row.original, event);
        }
        break;
      case ' ':
        if (selectable) {
          event.preventDefault();
          toggleRow(index, event.shiftKey);
        }
        break;
      case 'a':
        if (selectable && (event.metaKey || event.ctrlKey)) {
          event.preventDefault();
          if (!allSelected) toggleAll();
        }
        break;
      case 'Escape':
        if (selectable && selected.size > 0) {
          event.preventDefault();
          setSelected(new Set());
        }
        break;
    }
  };

  const onRowClickInternal = (event: MouseEvent<HTMLElement>, index: number) => {
    const target = event.target as HTMLElement;
    if (target.closest(INTERACTIVE) && target.closest(INTERACTIVE) !== event.currentTarget) return;
    setFocusIndex(index);
    const row = rows[index]!;
    if (selectable && event.shiftKey) {
      toggleRow(index, true);
      return;
    }
    if (selectable && (event.metaKey || event.ctrlKey)) {
      toggleRow(index, false);
      return;
    }
    if (onRowClick) onRowClick(row.original, event);
    else if (selectable) toggleRow(index, false);
  };

  const role = interactive ? 'grid' : 'table';
  const cellRole = interactive ? 'gridcell' : 'cell';
  const showEmpty = !loading && rows.length === 0;

  return (
    <div
      ref={scrollRef}
      role={role}
      aria-label={label}
      aria-rowcount={rows.length + 1}
      aria-colcount={leafColumns.length + (selectable ? 1 : 0)}
      aria-multiselectable={selectable || undefined}
      aria-busy={loading || undefined}
      // One tab stop: when the focusable row is scrolled out of the DOM, the container takes the stop instead.
      tabIndex={interactive && !focusRowRendered && rows.length > 0 ? 0 : undefined}
      onFocus={(e) => {
        if (e.target === e.currentTarget && rows.length > 0) focusRow(Math.max(firstRendered, 0));
      }}
      style={{ height: height ?? '100%' }}
      className={cn(
        'relative min-h-0 overflow-auto rounded-card border border-line bg-surface shadow-card gt-scroll',
        'overscroll-contain',
        className,
      )}
    >
      <div style={{ minWidth: totalWidth }} className="relative w-full">
        {/* Header */}
        {headerGroups.map((group) => (
          <div
            key={group.id}
            role="row"
            aria-rowindex={1}
            className="sticky top-0 z-[var(--gt-z-sticky)] flex w-full border-b border-line bg-surface-2/95 backdrop-blur"
            style={{ height: HEADER_HEIGHT }}
          >
            {selectable && (
              <div
                role="columnheader"
                className="flex shrink-0 items-center justify-center"
                style={cellStyle(SELECT_COL_WIDTH, 0)}
              >
                <HeaderCheckbox
                  checked={allSelected}
                  indeterminate={someSelected}
                  disabled={selectableRows.length === 0}
                  onToggle={toggleAll}
                />
              </div>
            )}
            {group.headers.map((header) => {
              const meta = metaOf(header.column);
              const sortable = header.column.getCanSort();
              const dir = header.column.getIsSorted();
              const sortIndex = sorting.length > 1 ? header.column.getSortIndex() : -1;
              const content = header.isPlaceholder
                ? null
                : flexRender(header.column.columnDef.header, header.getContext());
              return (
                <div
                  key={header.id}
                  role="columnheader"
                  aria-sort={dir === 'asc' ? 'ascending' : dir === 'desc' ? 'descending' : sortable ? 'none' : undefined}
                  className={cn(
                    'flex min-w-0 items-center px-3 text-[11px] font-semibold tracking-[0.05em] text-fg-muted uppercase',
                    ALIGN[meta.align ?? 'left'],
                    meta.headerClassName,
                  )}
                  style={cellStyle(header.getSize(), meta.grow)}
                >
                  {sortable ? (
                    <button
                      type="button"
                      onClick={header.column.getToggleSortingHandler()}
                      title={
                        dir === 'asc'
                          ? 'Sorted ascending. Click to sort descending.'
                          : dir === 'desc'
                            ? 'Sorted descending. Click to clear.'
                            : 'Click to sort. Shift+click to add a secondary sort.'
                      }
                      className={cn(
                        'group -mx-1 inline-flex max-w-full items-center gap-1 rounded-md px-1 py-1 uppercase',
                        'hover:bg-surface-3 hover:text-fg',
                        dir && 'text-fg',
                        meta.align === 'right' && 'flex-row-reverse',
                      )}
                    >
                      <span className="truncate">{content}</span>
                      {dir === 'asc' ? (
                        <ArrowUp aria-hidden className="size-3.5 shrink-0 text-primary-text" />
                      ) : dir === 'desc' ? (
                        <ArrowDown aria-hidden className="size-3.5 shrink-0 text-primary-text" />
                      ) : (
                        <ChevronsUpDown
                          aria-hidden
                          className="size-3.5 shrink-0 opacity-0 transition-opacity group-hover:opacity-60"
                        />
                      )}
                      {sortIndex >= 0 && dir && (
                        <span className="text-[10px] text-fg-subtle tabular-nums">{sortIndex + 1}</span>
                      )}
                    </button>
                  ) : (
                    <span className="truncate">{content}</span>
                  )}
                </div>
              );
            })}
          </div>
        ))}

        {/* Body */}
        {loading ? (
          <div role="rowgroup">
            {Array.from({ length: 8 }, (_, i) => (
              <div
                key={i}
                aria-hidden
                className="flex w-full items-center border-b border-line last:border-b-0"
                style={{ height: rowHeight }}
              >
                {selectable && <div style={cellStyle(SELECT_COL_WIDTH, 0)} />}
                {leafColumns.map((c) => (
                  <div key={c.id} className="px-3" style={cellStyle(c.getSize(), metaOf(c).grow)}>
                    <Skeleton className="h-3.5" width={`${55 + ((i * 17 + c.getIndex() * 23) % 40)}%`} />
                  </div>
                ))}
              </div>
            ))}
          </div>
        ) : showEmpty ? null : (
          <div role="rowgroup" className="relative w-full" style={{ height: virtualizer.getTotalSize() }}>
            {items.map((item) => {
              const row = rows[item.index]!;
              const isSelected = selectable && selected.has(row.id);
              const isActive = activeRowId != null && activeRowId.toLowerCase() === row.id.toLowerCase();
              const rowSelectable = selectable && canSelect(row);
              return (
                <div
                  key={item.key}
                  role="row"
                  data-row-index={item.index}
                  aria-rowindex={item.index + 2}
                  aria-selected={selectable ? isSelected : undefined}
                  tabIndex={interactive ? (item.index === safeFocus ? 0 : -1) : undefined}
                  onFocus={(e) => {
                    if (e.target === e.currentTarget) setFocusIndex(item.index);
                  }}
                  onKeyDown={interactive ? (e) => onRowKeyDown(e, item.index) : undefined}
                  onMouseDown={(e) => {
                    // Shift+click would otherwise select page text.
                    if (e.shiftKey && selectable) e.preventDefault();
                  }}
                  onClick={interactive ? (e) => onRowClickInternal(e, item.index) : undefined}
                  className={cn(
                    'absolute top-0 left-0 flex w-full items-center border-b border-line text-sm text-fg',
                    'outline-none focus-visible:z-[1] focus-visible:shadow-[inset_0_0_0_2px_var(--gt-focus)]',
                    interactive && 'cursor-pointer',
                    isSelected
                      ? 'bg-primary-soft hover:bg-[color-mix(in_srgb,var(--gt-primary)_16%,var(--gt-surface))]'
                      : isActive
                        ? 'bg-info-soft'
                        : interactive && 'hover:bg-surface-2',
                    getRowClassName?.(row.original),
                  )}
                  style={{ height: item.size, transform: `translateY(${item.start - HEADER_HEIGHT}px)` }}
                >
                  {selectable && (
                    <div
                      role={cellRole}
                      className="flex shrink-0 items-center justify-center self-stretch"
                      style={cellStyle(SELECT_COL_WIDTH, 0)}
                    >
                      <input
                        type="checkbox"
                        data-row-action
                        tabIndex={-1}
                        aria-label="Select row"
                        checked={isSelected}
                        disabled={!rowSelectable}
                        onChange={() => {}}
                        onClick={(e) => {
                          e.stopPropagation();
                          setFocusIndex(item.index);
                          toggleRow(item.index, e.shiftKey);
                        }}
                        className="size-4 cursor-pointer accent-[var(--gt-primary)] disabled:cursor-not-allowed"
                      />
                    </div>
                  )}
                  {row.getVisibleCells().map((cell) => {
                    const meta = metaOf(cell.column);
                    return (
                      <div
                        key={cell.id}
                        role={cellRole}
                        className={cn(
                          'flex min-w-0 items-center overflow-hidden px-3',
                          ALIGN[meta.align ?? 'left'],
                          meta.cellClassName,
                        )}
                        style={cellStyle(cell.column.getSize(), meta.grow)}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        )}
      </div>
      {showEmpty && (
        // Outside the min-width box and sticky, so it stays centred in the visible width of a wide table.
        <div className="sticky left-0 w-full">
          {emptyState ?? (
            <EmptyState compact icon={<Inbox />} title="Nothing to show" message="No rows match right now." />
          )}
        </div>
      )}
    </div>
  );
}

function HeaderCheckbox({
  checked,
  indeterminate,
  disabled,
  onToggle,
}: {
  checked: boolean;
  indeterminate: boolean;
  disabled: boolean;
  onToggle: () => void;
}) {
  return (
    <input
      ref={(node) => {
        if (node) node.indeterminate = indeterminate;
      }}
      type="checkbox"
      aria-label={checked ? 'Deselect all rows' : 'Select all rows'}
      aria-checked={indeterminate ? 'mixed' : checked}
      checked={checked}
      disabled={disabled}
      onChange={onToggle}
      className="size-4 cursor-pointer accent-[var(--gt-primary)] disabled:cursor-not-allowed disabled:opacity-40"
    />
  );
}
